import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    Modal,
    Row,
    Col,
    Form,
} from "react-bootstrap";
import validate from "validate.js";
import {
  hideWeeklyTimesheetValidationError,
  createTeamMonthlyTimesheets,
} from "actions";
import { ProgramDropdown, TimesheetTaskDropdown, UserDropdown } from "CommonDropdowns";
import moment from "moment";
import { isEmpty } from "lodash";

const schema = {
  program_id: {
    presence: { allowEmpty: false, message: '^Program is required' },
  },
  user_ids: {
    presence: { allowEmpty: false, message: '^User is required' },
  },
  task_ids: {
    presence: { allowEmpty: false, message: '^Task is required' },
  },
  month_year: {
    presence: { allowEmpty: false, message: '^Month is required' },
  }
};

const getMonthOptions = () => {
  const options = [];
  const start = moment().add(1, "month").startOf("month");

  for (let i = 0; i < 24; i++) {
    const m = start.clone().subtract(i, "months");
    options.push(m.format("YYYY-MM"));
  }

  return options;
};

const AddTimeModal = ({ show, handleClose, setCurrentMonth, extraFiltersState, setExtraFiltersState }) => {
    
    const dispatch = useDispatch();

    const session = useSelector((s) => s.session);
    const weeklyTimesheetState = useSelector((s) => s.weeklyTimesheetState);
    const object_viewed_id = session?.current_page_permissions?.object_id || "";

    const [monthOptions, setMonthOptions] = useState([]);
    const [formState, setFormState] = useState({
      isValid: false,
      values: {
        program_id: '',
        ['month_year']: extraFiltersState.values.month_year,
      },
      touched: {
        ['month_year']: true,
      },
      errors: {}
    });
    const [programValue, setProgramValue] = useState(null);
    const [taskValue, setTaskValue] = useState(null);
    const [userValue, setUserValue] = useState(null);

    useEffect(() => {
      setMonthOptions(getMonthOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => { 
      setFormState((formState) => ({
        ...formState,
        values: {
            ...formState.values,
            ['month_year']: extraFiltersState.values.month_year,
        },
        touched: {
            ...formState.touched,
            ['month_year']: true,
        },
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extraFiltersState.values.month_year]);

    
    useEffect(() => {
      if (weeklyTimesheetState.validation_error) {
        setFormState((formState) => ({
            ...formState,
            isValid: false,
            errors: weeklyTimesheetState.validation_error || {},
        }));
      }
    }, [weeklyTimesheetState.validation_error]);

    useEffect(() => {
      const errors = validate(formState.values, schema);
      setFormState(formState => ({
          ...formState,
          isValid: !errors,
          errors: errors || {}
      }));
    }, [formState.values]);

    const handleChange = (event) => {
      event.persist();
      setFormState((formState) => ({
        ...formState,
        values: {
            ...formState.values,
            [event.target.name]: event.target.value,
        },
        touched: {
            ...formState.touched,
            [event.target.name]: true,
        },
      }));
      dispatch(hideWeeklyTimesheetValidationError(event.target.name));
    };

    const handleProgramChange = (selected) => { 
      setProgramValue(selected);
      setFormState((formState) => ({
        ...formState,
        values: {
          ...formState.values,
          program_id: !isEmpty(selected) ? selected.value : '',
        },
        touched: {
          ...formState.touched,
          program_id: true,
        },
      }));
      // Reset user and task when program changes
      setUserValue(null);
      setTaskValue(null);
      setFormState((formState) => ({
        ...formState,
        values: {
          ...formState.values,
          user_ids: [],
          task_ids: [],
        },
      }));
      dispatch(hideWeeklyTimesheetValidationError('program_id'));
    }

    const handleUserChange = (selected) => {
      setUserValue(selected);
      setFormState((formState) => ({
        ...formState,
        values: {
          ...formState.values,
          user_ids: selected ? selected.map(item => item.value) : [],
        },
        touched: {
          ...formState.touched,
          user_ids: true,
        },
      }));
      // Reset task when user changes
      setTaskValue(null);
      setFormState((formState) => ({
        ...formState,
        values: {
          ...formState.values,
          task_ids: [],
        },
      }));
      dispatch(hideWeeklyTimesheetValidationError('user_ids'));
    }

    const handleTaskChange = (selected) => {
      setTaskValue(selected);
      setFormState((formState) => ({
        ...formState,
        values: {
            ...formState.values,
            task_ids: selected ? selected.map(item => item.value) : [],
        },
        touched: {
            ...formState.touched,
            task_ids: true,
        },
      }));
      dispatch(hideWeeklyTimesheetValidationError('task_ids'));
    }

    const onFormSubmit = (event) => {
      event.preventDefault();

      if(formState.isValid){
        dispatch(createTeamMonthlyTimesheets(formState.values, object_viewed_id))
        .then(() => {
          handleClose();
          setCurrentMonth(formState.values.month_year);

          setFormState({
            isValid: false,
            values: {
              month_year: formState.values.month_year,
            },
            touched: {
              month_year: true,
            },
            errors: {}
          });

          setProgramValue(null);
          setTaskValue(null);
          setUserValue(null);
        })
        .catch(() => {
          // Error is already handled by the action
        });
      }
    };

    const hasError = (field) =>
      formState.touched[field] && formState.errors[field] ? true : false;

    const onModelClose = () => {
      handleClose();

      setFormState({
        isValid: false,
        values: {
          month_year: extraFiltersState.values.month_year,
        },
        touched: {
          month_year: true,
        },
        errors: {}
      });

      setProgramValue(null);
      setTaskValue(null);
      setUserValue(null);
    }

    return (
        <Modal
            show={show}
            onHide={onModelClose}
            centered
            className="add-new-program"
        >
            <div className="p-2">
                <Modal.Header className="p-2" closeButton>
                    <Modal.Title>
                        <h5 className="font-20 font-weight-bold heading-color">
                            Add Time
                        </h5>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form onSubmit={onFormSubmit}>
                        
                    <Form.Group className="mb-3">
                            <Form.Label className="label-input">Month</Form.Label>
                            <Form.Select 
                                className={`input-field ${hasError('month_year') ? 'is-invalid' : ''}`}
                                name="month_year"
                                onChange={handleChange}
                                value={formState.values.month_year || ''}
                            >
                              <option value="">Select Month</option>
                              {monthOptions.map(month_year => (
                                <option key={month_year} value={month_year}>{moment(month_year, 'YYYY-MM').format('MMMM YYYY')}</option>
                              ))}
                            </Form.Select>
                            {hasError('month_year') && (
                                <Form.Control.Feedback type="invalid">
                                    {formState.errors.month_year[0]}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        
                        <ProgramDropdown
                          programValue={programValue}
                          programOnChange={handleProgramChange}
                          setProgramValue={setProgramValue}
                          isMulti={false}
                          trackingProgram={1}
                          loadManagerPrograms={true}
                          error={hasError('program_id') ? formState.errors.program_id[0] : ''}
                        />

                        {formState.values.program_id && (
                          <>
                            <UserDropdown
                              userValue={userValue}
                              userOnChange={handleUserChange}
                              setUserValue={setUserValue}
                              isMulti={true}
                              showSelectAllOption={false}
                              selectedId={formState.values.user_ids}
                              loadUnbindedOnly={false}
                              loadManagerProgramUsers={true}
                              programId={formState.values.program_id}
                              error={hasError('user_ids') ? formState.errors.user_ids[0] : ''}
                            />
                            {hasError("user_ids") && (
                                <div className="invalid-feedback d-block">
                                    {formState.errors.user_ids[0]}
                                </div>
                            )}
                          </>
                        )}

                        {formState.values.program_id && formState.values.user_ids && formState.values.user_ids.length > 0 && (
                          <>
                            <TimesheetTaskDropdown
                                taskValue={taskValue}
                                taskOnChange={handleTaskChange}
                                setTaskValue={setTaskValue}
                                isMulti={true}
                                programId={formState.values.program_id}
                                user_ids={formState.values.user_ids}
                                showSelectAllOption={false}
                                loadUnbinded={false}
                                error={hasError('task_ids') ? formState.errors.task_ids[0] : ''}
                            />
                            {hasError("task_ids") && (
                              <div className="invalid-feedback d-block">
                                {formState.errors.task_ids[0]}
                              </div>
                            )}
                          </>
                        )}

                        

                        {/* Action Buttons */}
                        <Row className="mt-5">
                            <Col lg={6}>
                                <button
                                    type="button"
                                    className="c-b-btn w-100"
                                    onClick={onModelClose}
                                >
                                    Cancel
                                </button>
                            </Col>
                            <Col lg={6}>
                                <button
                                    type="submit"
                                    className="n-u-btn w-100"
                                    disabled={!formState.isValid}
                                >
                                    Add
                                </button>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
            </div>
        </Modal>
    );
};

export default AddTimeModal;
